import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Instagram } from "lucide-react";
import { NavyogaLogo } from "./NavyogaLogo";
import { SITE } from "@/lib/site";
import { scrollToSection } from "./Header";

const QUICK_LINKS = [
  { label: "About", href: "#about" },
  { label: "Therapies", href: "#therapies" },
  { label: "Schedule", href: "#schedule" },
  { label: "Gallery", href: "#classes" },
  { label: "Awards", href: "#awards" },
  { label: "Community", href: "#stories" },
  { label: "Contact", href: "#contact" },
];

export const Footer = () => (
  <footer data-testid="nav-footer" className="bg-[#0F2440] text-slate-300">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
      <div className="grid gap-12 md:grid-cols-3">
        <div>
          <NavyogaLogo className="h-14" flat />
          <p className="font-heading mt-5 text-xl italic text-[#F8FFF8]">“{SITE.tagline}”</p>
          <span className="mt-4 inline-block rounded-full border border-[#2E8B57]/40 bg-[#2E8B57]/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">
            7+ Years of Experience
          </span>
          <a
            data-testid="footer-instagram-follow-button"
            href={SITE.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 flex w-fit items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-[#F8FFF8] transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 hover:border-emerald-300/50 hover:bg-white/10"
          >
            <Instagram className="h-4 w-4 text-emerald-300" />
            Follow @{SITE.instagram}
          </a>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Explore</p>
          <ul className="mt-5 space-y-3">
            {QUICK_LINKS.map((link) => (
              <li key={link.href}>
                <button
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => scrollToSection(link.href)}
                  className="text-sm text-slate-300 transition-colors duration-300 hover:text-emerald-300"
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Reach Us</p>
          <ul className="mt-5 space-y-3 text-sm">
            {SITE.addressLines.map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#2E8B57]" />
                {line}
              </li>
            ))}
            <li className="flex items-center gap-2.5">
              <Phone className="h-4 w-4 shrink-0 text-[#2E8B57]" />
              {SITE.phoneDisplay}
            </li>
            <li className="flex items-center gap-2.5">
              <Mail className="h-4 w-4 shrink-0 text-[#2E8B57]" />
              {SITE.email}
            </li>
            <li>
              <a
                data-testid="footer-instagram-link"
                href={SITE.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 transition-colors duration-300 hover:text-emerald-300"
              >
                <Instagram className="h-4 w-4 shrink-0 text-[#2E8B57]" />
                @{SITE.instagram}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 border-t border-white/10 pt-8">
        <p data-testid="footer-disclaimer" className="text-xs leading-relaxed text-slate-400">
          Disclaimer: The therapies offered at NAVYOGA are complementary wellness practices and are not a substitute for
          professional medical advice, diagnosis or treatment. Please consult your physician before beginning any new
          therapy or exercise programme.
        </p>
        <div className="mt-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} {SITE.fullName}. All rights reserved.</p>
          <Link
            to="/admin"
            data-testid="admin-portal-open-button"
            className="text-xs font-medium text-slate-500 transition-colors hover:text-emerald-300"
          >
            Studio Login
          </Link>
        </div>
      </div>
    </div>
  </footer>
);
